import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Card from '../../components/Card/Card';
import Input from '../../components/Input/Input';
import Button from '../../components/Button/Button';
import Loading from '../../components/Loading/Loading';
import api from '../../services/api';
import { formatDateForInput, getErrorMessage } from '../../utils/helpers';
import './EventForm.scss';

const EventForm: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isEdit = Boolean(eventId);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(isEdit);
  const [error, setError] = useState('');

  const fetchEvent = useCallback(async () => {
    if (!user?.uuid || !eventId) return;

    try {
      setFetchLoading(true);
      const event = await api.getEvent(user.uuid, eventId);
      setTitle(event.title);
      setDescription(event.description || '');
      setStartDate(formatDateForInput(event.startDate));
      setEndDate(event.endDate ? formatDateForInput(event.endDate) : '');
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setFetchLoading(false);
    }
  }, [user, eventId]);

  useEffect(() => {
    if (isEdit && eventId) {
      fetchEvent();
    }
  }, [isEdit, eventId, fetchEvent]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.uuid) return;

    setError('');
    setLoading(true);

    try {
      const eventData = {
        title,
        description: description || undefined,
        startDate: new Date(startDate).toISOString(),
        endDate: endDate ? new Date(endDate).toISOString() : undefined,
      };

      if (isEdit && eventId) {
        await api.updateEvent(user.uuid, eventId, eventData);
        navigate(`/events/${eventId}`);
      } else {
        const newEvent = await api.createEvent(user.uuid, eventData);
        navigate(`/events/${newEvent.uuid}`);
      }
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return <Loading message="Loading event..." />;
  }

  return (
    <div className="event-form-page">
      <div className="container">
        <Card className="event-form-card">
          <h1>{isEdit ? 'Edit Event' : 'Create Event'}</h1>

          {error && <div className="error-message">{error}</div>}

          <form onSubmit={handleSubmit}>
            <Input
              label="Event Title"
              type="text"
              name="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              placeholder="Enter event title"
            />

            <Input
              label="Description"
              name="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter event description (optional)"
              multiline
              rows={4}
            />

            <Input
              label="Start Date and Time"
              type="datetime-local"
              name="startDate"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              required
            />

            <Input
              label="End Date and Time (Optional)"
              type="datetime-local"
              name="endDate"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              placeholder="Leave blank if single date/time"
            />

            <div className="form-actions">
              <Button
                variant="outline"
                onClick={() => navigate(isEdit ? `/events/${eventId}` : '/events')}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Saving...' : isEdit ? 'Update Event' : 'Create Event'}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default EventForm;
